import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Container } from '@sorare/core/src/atoms/layout/Container';
import { NBA_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';

import { Layout } from '@sorare/us-sports/src/components/competitionDetails/Layout';
import {
  RewardPool,
  useRewardPoolFilters,
} from '@sorare/us-sports/src/components/lobby/RewardPool';

import {
  NBACompetitionDetailsPageRewardsQuery,
  NBACompetitionDetailsPageRewardsQueryVariables,
} from './__generated__/index.graphql';

export const COMPETITION_DETAILS_PAGE_REWARDS_QUERY = gql`
  query NBACompetitionDetailsPageRewardsQuery(
    $leaderboardSlug: String!
    $cardRarity: CardRarity!
    $tier: Int!
  ) {
    nbaLeaderboard(slug: $leaderboardSlug) {
      slug
      fixture {
        slug
        fixtureState
        rewardPool(cardRarity: $cardRarity, tier: $tier) {
          slug
          ...RewardPool_PlayerInterface
        }
      }
    }
  }
  ${RewardPool.fragments.PlayerInterface}
` as TypedDocumentNode<
  NBACompetitionDetailsPageRewardsQuery,
  NBACompetitionDetailsPageRewardsQueryVariables
>;

export const CompetitionDetailsPageRewards = () => {
  const { competition, leaderboardSlug = '' } = useParams();

  const slug = competition ?? leaderboardSlug;
  const [filters, setFilters] = useRewardPoolFilters();

  const { data, loading } = useQuery(COMPETITION_DETAILS_PAGE_REWARDS_QUERY, {
    skip: !slug,
    variables: {
      leaderboardSlug: slug,
      cardRarity: filters.scarcity,
      tier: parseInt(filters.tier, 10),
    },
  });

  if (!slug) return null;

  const { nbaLeaderboard } = data ?? {};
  const { fixture } = nbaLeaderboard ?? {};
  const { fixtureState, rewardPool } = fixture ?? {};

  return (
    <Layout
      fullWidth
      backLink={
        <FormattedMessage id="RewardPool.title" defaultMessage="Rewards" />
      }
    >
      <Container>
        <RewardPool
          loading={loading}
          playerRoute={NBA_PLAYERS_SLUG}
          fixtureState={fixtureState}
          players={rewardPool}
          filters={filters}
          title={null}
          onFilterChange={setFilters}
        />
      </Container>
    </Layout>
  );
};
